export const MATRIX_RECOVERY_MESSAGE = "Encrypted chat could not start because this device's encryption storage could not be verified and automatic device migration did not complete. The chat list remains available, but encrypted message content requires a successful migration; sign out and sign in again to retry.";

export class MatrixContinuityError extends Error {
    constructor() {
        super(MATRIX_RECOVERY_MESSAGE);
        this.name = "MatrixContinuityError";
    }
}

const GENERATION_KEY = "dailyfixme.crypto-store-generation";
const CORE_STORE = "core";

// Read the SDK's existing database without ever creating an empty replacement.
// The account is checked with count(), so no private crypto data leaves IndexedDB.
const openExistingDatabase = (name: string): Promise<IDBDatabase> => new Promise((resolve, reject) => {
    let blocked: boolean = false;
    const request: IDBOpenDBRequest = indexedDB.open(name);
    request.onupgradeneeded = () => request.transaction?.abort();
    request.onerror = () => reject(new MatrixContinuityError());
    request.onblocked = () => { blocked = true; reject(new MatrixContinuityError()); };
    request.onsuccess = () => {
        if (blocked) {
            request.result.close();
            return;
        }
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
    };
});

export const checkCryptoStoreAccountExists = async (prefix: string): Promise<void> => {
    let database: IDBDatabase | undefined;
    try {
        database = await openExistingDatabase(`${prefix}::matrix-sdk-crypto`);
        await new Promise<void>((resolve, reject) => {
            const transaction: IDBTransaction = database!.transaction(CORE_STORE, "readonly");
            const account: IDBRequest<number> = transaction.objectStore(CORE_STORE).count("account");
            transaction.oncomplete = () => account.result === 1 ? resolve() : reject(new MatrixContinuityError());
            transaction.onabort = () => reject(new MatrixContinuityError());
            transaction.onerror = () => reject(new MatrixContinuityError());
        });
    } catch {
        throw new MatrixContinuityError();
    } finally {
        database?.close();
    }
};

export const checkCryptoStoreGeneration = async (
    prefix: string,
    generation: string,
    initialize: boolean,
): Promise<void> => {
    let database: IDBDatabase | undefined;
    try {
        database = await openExistingDatabase(`${prefix}::matrix-sdk-crypto`);
        await new Promise<void>((resolve, reject) => {
            const transaction: IDBTransaction = database!.transaction(CORE_STORE, initialize ? "readwrite" : "readonly");
            const store: IDBObjectStore = transaction.objectStore(CORE_STORE);
            const account: IDBRequest<number> = store.count("account");
            const marker: IDBRequest = store.get(GENERATION_KEY);
            marker.onsuccess = () => {
                if (account.result !== 1 || (marker.result !== generation && (!initialize || marker.result !== undefined))) {
                    transaction.abort();
                    return;
                }
                if (initialize) {
                    store.put(generation, GENERATION_KEY);
                }
            };
            transaction.oncomplete = () => resolve();
            transaction.onabort = () => reject(new MatrixContinuityError());
            transaction.onerror = () => reject(new MatrixContinuityError());
        });
    } catch {
        throw new MatrixContinuityError();
    } finally {
        database?.close();
    }
};

export const createMatrixGuardedFetch = (
    fetchRequest: typeof fetch,
    onConflict: () => void,
): typeof fetch => {
    let failed: boolean = false;
    return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        if (failed) {
            throw new MatrixContinuityError();
        }
        const response: Response = await fetchRequest(input, init);
        const url: string = input instanceof Request ? input.url : String(input);
        if (/\/_matrix\/client\/[^/]+\/keys\/upload$/.test(new URL(url).pathname) && response.status === 400) {
            const body: { errcode?: string; error?: string } = await response.clone().json().catch(() => ({}));
            if (typeof body.error === "string" && /one time key .*already exists/i.test(body.error)) {
                failed = true;
                onConflict();
                // Never pass Synapse's old/new key material into SDK error logging.
                return new Response(JSON.stringify({ errcode: "M_BAD_JSON", error: MATRIX_RECOVERY_MESSAGE }), {
                    status: 400,
                    headers: { "Content-Type": "application/json" },
                });
            }
        }
        return response;
    };
};
